import LangChange from "@components/Home/LangChange";
import { buildClass } from "@utils/tag";
import useTranslation from "next-translate/useTranslation";
import React, { useCallback, useState } from "react";
import RegisterForm from "./RegisterForm";
import { Typography } from "@mui/material";
import { useDispatch } from "noval";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { RegisterSchema } from "./helper";
import Link from "next/link";
import { handleError } from "@network/handle-errors";
import { http } from "@network/http";
import dayjs from "dayjs";
import { toast } from "react-toastify";

const RegisterFormWrapper = ({ setOtpModal }) => {
  const { push } = useRouter();
  const { dispatch } = useDispatch();
  const { t } = useTranslation("register");
  const [loading, setLoading] = useState(false);

  const { watch, control, handleSubmit, setError } = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues: {
      confirmed: false,
      phone_country_iso2: "SA",
    },
  });

  const onResend = useCallback(async ({ vid, token, resendData }) => {
    setLoading(true);
    try {
      const { data } = await http.post(
        `/resend-verify-code`,
        { vid },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const newVid = data?.data?.vid || "";
      toast.success(data?.message);

      setOtpModal({
        open: true,
        data: { ...resendData, vid: newVid },
        digitsLength: 6,
        callback: (res) => {
          setOtpModal({ open: false });
          const jwt = res.data?.data?.token;
          window.localStorage.setItem("jwt", jwt);
          push("/home");
          dispatch({ reminderOpen: true });
        },
        api_url: "/absher/verify",
        onResend: () => onResend({ vid: newVid, token, resendData }),
      });
    } catch (error) {
      handleError(error, { setError });
    }
    setLoading(false);
  }, []);

  const handleRegister = useCallback(async (values) => {
    setLoading(true);
    try {
      if (values.birth_date) {
        values.birth_date = dayjs(values.birth_date).format("YYYY-MM-DD");
      }
      delete values.confirmed;
      const { data } = await http.post("/register", values);
      const vid = data?.data?.vid || "";
      const token = data?.data?.token || "";

      setOtpModal({
        open: true,
        data: { ...values, vid },
        api_url: "/absher/verify",
        digitsLength: 6,
        callback: (res) => {
          setOtpModal({ open: false });
          const jwt = res.data?.data?.token;
          window.localStorage.setItem("jwt", jwt);
          dispatch("authentication", {
            callback: ({ is_kyc_completed, is_iqama_updated }) => {
              push("/home");
              setOtpModal({ open: false });
              if (!is_iqama_updated) {
                dispatch({ iqamaAlert: true });
              }
              if (!is_kyc_completed) {
                dispatch({ reminderOpen: true });
              } else {
                dispatch({ isOpen: true, suitable: false }, "suitability");
              }
            },
          });
        },
        onResend: () => onResend({ vid, token, resendData: values }),
      });
    } catch (error) {
      handleError(error, { setError });
    }
    setLoading(false);
  }, []);

  return (
    <div
      style={{ flexBasis: "50%" }}
      className={buildClass(
        "bg-[#f8fbff] w-[100%] md:w-[700px] mx-auto",
        "lg:w-full rounded-t-[30px] sm:rounded-[30px]",
        "lg:rounded-e-none lg:rounded-s-[30px]",
        "lg:px-[91px] pt-[60px] pb-[82px] lg:mb-0",
        "lg:max-h-screen lg:overflow-y-scroll px-5 md:px-7"
      )}
    >
      <div className="flex justify-end items-center">
        <LangChange showFullTitle />
      </div>
      <h1
        className={buildClass(
          "font-medium text-[#333333] text-[25px]",
          "sm:text-[34px] leading-[41px] font-Expo_sim mb-10"
        )}
      >
        {t("form_title")}
      </h1>
      <RegisterForm
        watch={watch}
        loading={loading}
        control={control}
        handleSubmit={handleSubmit}
        handleRegister={handleRegister}
      />
      <Typography
        className={buildClass(
          "!mt-[20px] flex justify-center gap-2",
          "!text-[13px] sm:!text-base !font-normal text-[#8F8F8F]"
        )}
      >
        {t("haveAccount")}
        <Link className="!font-medium text-[#0366FF] capitalize" href="/login">
          {t("signIn")}
        </Link>
      </Typography>
    </div>
  );
};

export default RegisterFormWrapper;
